import axios from 'axios';
import {saveAs} from 'file-saver';
import {userStore} from '../stores/user.store';
import config from 'config';

const base = config.apiBase;

const endpoints = {
  copyAppApi: `${base}/v3/app/copy`,
  deleteDraftApi: `${base}/v3/app/partial`,
  getAppVersionsApi: `${base}/v3/app/versions`,
  loadSavedAppApi: `${base}/v3/app/partialdata`,
  partialSaveApi: `${base}/v3/app/partialdata`,
  getMyApps: `${base}/v4/apps/developer`,
  uploadScreenshotApi: `${base}/v3/app/image`,
  uploadPackageApi: `${base}/v3/app/binary`,
  uploadDeveloperChecklistApi: `${base}/v3/app/certificationfiles`,
  downloadDeveloperChecklistApi: `${base}/v3/app/developer/checklist`,
  userProfile: `${base}/user/profile`,
  logout: `${base}/user/logout`,
  removeAppApi: `${base}/v3/removeapp`,
  submitAppApi: `${base}/v3/app`,
  editAppApi: `${base}/v3/edit/app`,
  withdrawAppApi: `${base}/v3/app/withdraw`,
  deleteAppPackageApi: `${base}/v3/app/partial/binary`,
  deleteDeveloperChecklistApi: `${base}/v3/app/certificationfiles`,
  feedbackApi: `${base}/contact`,
};

//Add Bearer token to all API calls aside from profile service
axios.interceptors.request.use(request => {
  if (request.url !== endpoints.userProfile && request.url !== endpoints.logout) {
    request.headers['Authorization'] = `Bearer ${userStore.api_token}`;
  }

  if (location.host.indexOf('localhost') !== -1 && request.url.indexOf('/api') !== -1) {
    request.url = 'http://localhost:8080' + request.url;
    
  }
  
  return request;
});

//Handle auth errors from all requests
axios.interceptors.response.use((response) => {
  return response;
}, (error) => {

  if (error.response) {
    let response = error.response;
    let old_req = response.config;

    if (response.status === 401 && old_req.url !== endpoints.userProfile) {
      return userStore.revalidateUser().then(() => axios(old_req));
    }
  }

  throw error;
});

class ApiService {

  validateUserData() {
    return axios.get(endpoints.userProfile, {
      withCredentials: true
    });
  }

  logoutUser() {
    return axios.get(endpoints.logout, {
      withCredentials: true
    });
  }

  copyApp(configObj) {
    return axios.post(endpoints.copyAppApi, configObj);
  }

  deleteDraft(saveId) {
    return axios.delete(`${endpoints.deleteDraftApi}/${saveId}`);
  }

  getAppVersions(applicationId) {
    return axios.get(`${endpoints.getAppVersionsApi}?applicationId=${applicationId}`);
  }

  getMyApps() {
    return axios.get(`${endpoints.getMyApps}`);
  }

  getCategoriesAndSegments() {
    return axios.get(`${base}/categoriesandsegments`);
  }

  getAppCapabilities() {
    return axios.get(`${base}/capabilities`);
  }

  loadSavedApp(saveId) {
    return axios.get(`${endpoints.loadSavedAppApi}?saveId=${saveId}`);
  }

  partialSave(obj) {
    return axios.post(endpoints.partialSaveApi, obj);
  }

  removeApp(appPsk, reason, saveId) {
    return axios.post(endpoints.removeAppApi, {
      appPsk: appPsk,
      removalReason: reason,
      saveId: saveId
    });
  }

  submitApp(saveId) {
    return axios.post(endpoints.submitAppApi, {
      saveId: saveId
    });
  }

  editApp(newAppData) {
    // Note: No need to pass an applicationIdentifier; it's present in the data object.
    return axios.put(endpoints.editAppApi, newAppData);
  }

  uploadScreenshot(file, applicationIdentifier, updateProgress, deviceType) {
    // Note: applicationIdentifier can be either saveId or appPsk_VersionNumber.
    const deviceTypeEndpoint = deviceType.toUpperCase();
    const url = `${endpoints.uploadScreenshotApi}/${applicationIdentifier}/1/${deviceTypeEndpoint}`;
    return axios.post(url, file, {
      headers: {
        'Content-Type': 'image/png'
      },
      onUploadProgress(progressEvent) {
        updateProgress.progress = (Math.ceil(100 * progressEvent.loaded / progressEvent.total));
      }
    })
  }

  uploadPackage(file, saveId, updateProgress) {
    return axios.post(`${endpoints.uploadPackageApi}/${saveId}`, file, {
      headers: {
        'Content-Type': 'application/octet-stream'
      },
      onUploadProgress(progressEvent) {
        updateProgress.progress = (Math.ceil(100 * progressEvent.loaded / progressEvent.total));
      }
    })
  }

  deleteAppPackage(saveId) {
    return axios.delete(`${endpoints.deleteAppPackageApi}/${saveId}`);
  }

  uploadDeveloperChecklistFile(payloadExtracts, saveId, updateProgress) {
    return axios.post(`${endpoints.uploadDeveloperChecklistApi}/${saveId}/${encodeURIComponent(payloadExtracts.name)}/${payloadExtracts.extension}`, payloadExtracts.binary, {
      headers: {
        'Content-Type': 'application/octet-stream'
      },
      onUploadProgress(progressEvent) {
        updateProgress.progress = (Math.ceil(100 * progressEvent.loaded / progressEvent.total));
      }
    })
  }

  downloadDeveloperChecklistFile({applicationIdentifier, versionNumber}) {
    const idParam = `applicationIdentifier=${applicationIdentifier}`;
    const versionParam = versionNumber ?
      `versionNumber=${versionNumber}` :
      '';

    return axios.get(`${endpoints.downloadDeveloperChecklistApi}?${idParam}&${versionParam}`, {
      responseType: 'blob'
    }).then((response) => {
      const suggestedFileName = response.headers["content-disposition"].split('=')[1];
      const effectiveFileName = suggestedFileName || 'AppReviewAndCertificationForm';
      saveAs(response.data, effectiveFileName);
    }).catch((error) => {
      console.error("Could not download the file.", error);
    });
  }

  deleteDeveloperChecklist(saveId) {
    return axios.delete(`${endpoints.deleteDeveloperChecklistApi}/${saveId}`);
  }

  submitCustomerFeedbackForm(feedbackObject) {
    return axios.post(endpoints.feedbackApi, feedbackObject);
  }

  withdrawApp(saveId) {
    const obj = {
      saveId: saveId
    };
    return axios.post(endpoints.withdrawAppApi, obj);
  }
}

export const apiService = new ApiService();
